<template>
  <div class="mx-auto w-full max-w-3xl px-5 py-6 text-base text-slate-800 dark:text-slate-200">
    <div v-if="loading" class="py-12 text-sm text-slate-500 dark:text-slate-400">
      Loading task...
    </div>

    <div v-else-if="task" class="space-y-7">
      <input
        ref="titleInputRef"
        v-model="draft.title"
        type="text"
        class="block w-full bg-transparent text-2xl font-semibold leading-tight text-slate-950 outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-500 dark:text-slate-100 dark:disabled:text-slate-500"
        :disabled="!canEdit"
        required
        aria-label="Task title"
        @blur="saveDirtyFields(['title'])"
        @keydown.enter.prevent="commitTitle"
      >

      <div class="flex flex-wrap items-center gap-2">
        <span
          v-if="draft.ref"
          class="inline-flex min-h-8 items-center bg-slate-100 px-3 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          {{ draft.ref }}
        </span>

        <Dropdown
          v-model="draft.status"
          :options="statusOptions"
          :disabled="!canEdit"
          :trigger-class="chipButtonClass(!canEdit)"
          @select="saveDirtyFields(['status'])"
        >
          <template #trigger>
            <span :class="statusDotClass(draft.status)" aria-hidden="true" />
            <span>{{ statusLabel(draft.status) }}</span>
          </template>
          <template #option="{ option, selected }">
            <span :class="statusDotClass(option.value)" aria-hidden="true" />
            <span class="flex-1">{{ option.label }}</span>
            <span v-if="selected" class="text-xs font-semibold text-indigo-700 dark:text-indigo-400">Selected</span>
          </template>
        </Dropdown>

        <Dropdown
          v-model="draft.project_id"
          :options="projectOptions"
          :disabled="!canEdit"
          :trigger-class="chipButtonClass(!canEdit)"
          @select="saveDirtyFields(['project_id'])"
        >
          <template #trigger>
            <span class="h-2.5 w-2.5 bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
            <span>{{ projectName(draft.project_id) }}</span>
          </template>
          <template #option="{ option, selected }">
            <span class="flex-1 truncate">{{ option.label }}</span>
            <span v-if="selected" class="text-xs font-semibold text-indigo-700 dark:text-indigo-400">Selected</span>
          </template>
        </Dropdown>

        <button
          type="button"
          :class="chipButtonClass(!canMoveTask)"
          :disabled="!canMoveTask"
          @click="toggleMovePanel"
        >
          Move
        </button>

        <Dropdown
          v-model="draft.planning_state"
          :options="planningStateOptions"
          :disabled="!canEdit"
          :trigger-class="chipButtonClass(!canEdit)"
          @select="saveDirtyFields(['planning_state'])"
        >
          <template #trigger>
            <span>{{ planningStateLabel(draft.planning_state) }}</span>
          </template>
          <template #option="{ option, selected }">
            <span class="flex-1">{{ option.label }}</span>
            <span v-if="selected" class="text-xs font-semibold text-indigo-700 dark:text-indigo-400">Selected</span>
          </template>
        </Dropdown>

        <button
          v-if="canParkTask"
          type="button"
          :class="chipButtonClass(parkingTask)"
          :disabled="parkingTask"
          @click="toggleParkScores"
        >
          Park it
        </button>

        <GutScorePicker
          v-if="showParkScores && canParkTask"
          class="basis-full pt-1"
          :disabled="parkingTask"
          @pick="parkWithScore"
        />

        <p
          v-if="parkMessage"
          class="basis-full text-xs"
          :class="parkMessageClass"
          role="status"
        >
          {{ parkMessage }}
        </p>

        <div
          v-if="showMovePanel"
          class="basis-full border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900"
          @click.stop
        >
          <div class="flex flex-wrap items-center gap-2">
            <Dropdown
              v-model="moveWorkspaceSlug"
              :options="moveWorkspaceOptions"
              :disabled="movingTask"
              :trigger-class="moveDropdownClass(movingTask)"
              @select="handleMoveWorkspaceSelect"
            >
              <template #trigger>
                <span class="truncate">{{ moveWorkspaceName }}</span>
                <svg class="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <path d="M4.5 6 8 9.5 11.5 6h-7Z" />
                </svg>
              </template>
              <template #option="{ option, selected }">
                <span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
                <span v-if="selected" class="text-xs font-semibold text-indigo-700 dark:text-indigo-400">Selected</span>
              </template>
            </Dropdown>

            <Dropdown
              v-model="moveProjectSlug"
              :options="moveProjectOptions"
              :disabled="movingTask || !selectedMoveTarget"
              :trigger-class="moveDropdownClass(movingTask || !selectedMoveTarget)"
              @select="moveError = ''"
            >
              <template #trigger>
                <span class="truncate">{{ moveProjectName }}</span>
                <svg class="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                  <path d="M4.5 6 8 9.5 11.5 6h-7Z" />
                </svg>
              </template>
              <template #option="{ option, selected }">
                <span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
                <span v-if="selected" class="text-xs font-semibold text-indigo-700 dark:text-indigo-400">Selected</span>
              </template>
            </Dropdown>

            <button
              type="button"
              class="inline-flex min-h-9 items-center bg-indigo-600 px-3 text-sm font-semibold text-slate-50 outline-none hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
              :disabled="!canSubmitMove"
              @click="submitMove"
            >
              {{ movingTask ? 'Moving' : 'Move task' }}
            </button>

            <button
              type="button"
              class="inline-flex min-h-9 items-center px-2 text-sm font-medium text-slate-600 outline-none hover:text-slate-950 focus:ring-2 focus:ring-indigo-500 dark:text-slate-400 dark:hover:text-slate-100"
              :disabled="movingTask"
              @click="cancelMove"
            >
              Cancel
            </button>
          </div>

          <p
            v-if="moveError"
            class="mt-2 text-xs text-red-700 dark:text-red-300"
            role="alert"
          >
            {{ moveError }}
          </p>
          <p
            v-else-if="moveMessage"
            class="mt-2 text-xs text-slate-500 dark:text-slate-400"
            role="status"
          >
            {{ moveMessage }}
          </p>
        </div>

        <div
          v-if="editingEffort"
          class="inline-flex min-h-8 items-center bg-slate-100 px-3 text-sm font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          <input
            ref="effortInputRef"
            v-model.number="draft.effort"
            type="number"
            min="0"
            step="0.25"
            class="w-16 bg-transparent text-sm font-medium text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-500 dark:text-slate-100 dark:disabled:text-slate-500"
            :disabled="!canEdit"
            aria-label="Effort hours"
            @blur="finishEffort"
            @keydown.enter.prevent="finishEffort"
            @keydown.escape.prevent="cancelEffort"
          >
          <span class="text-slate-500 dark:text-slate-400">h</span>
        </div>
        <button
          v-else
          type="button"
          :class="chipButtonClass(!canEdit)"
          :disabled="!canEdit"
          @click="startEffort"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m15 12-8.373 8.373a1 1 0 1 1-3-3L12 9"/>
            <path d="m18 15 4-4"/>
            <path d="m21.5 11.5-1.914-1.914A2 2 0 0 1 19 8.172V7l-2.26-2.26a6 6 0 0 0-4.202-1.756L9 2.96l.92.82A6.18 6.18 0 0 1 12 8.4V10l2 2h1.172a2 2 0 0 1 1.414.586L18.5 14.5"/>
          </svg>
          {{ formatHours(draft.effort) }}
        </button>

        <span
          v-for="tag in visibleTags"
          :key="tag"
          class="inline-flex min-h-7 items-center gap-1 border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          <span>{{ tag }}</span>
          <button
            v-if="canEdit"
            type="button"
            class="ml-1 text-sm font-semibold text-slate-500 outline-none hover:text-red-700 focus:ring-2 focus:ring-red-500 dark:text-slate-400 dark:hover:text-red-300"
            :aria-label="`Remove ${tag}`"
            @click="removeTag(tag)"
          >
            ×
          </button>
        </span>

        <form
          v-if="addingTag"
          class="inline-flex min-h-8 items-center bg-slate-100 px-3 dark:bg-slate-800"
          @submit.prevent="addTag"
          @click.stop
        >
          <input
            ref="tagInputRef"
            v-model.trim="newTag"
            type="text"
            class="w-24 bg-transparent text-sm font-medium text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100"
            aria-label="New tag"
            @blur="finishTagInput"
            @keydown.escape.prevent="cancelTagInput"
          >
        </form>
        <button
          v-else-if="canEdit"
          type="button"
          class="inline-flex min-h-8 items-center bg-slate-50 px-3 text-sm font-medium text-slate-500 outline-none hover:bg-slate-100 hover:text-slate-950 focus:ring-2 focus:ring-indigo-500 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
          @click="startTagInput"
        >
          + tag
        </button>

        <button
          v-if="!canEdit && lockHolder"
          type="button"
          class="inline-flex min-h-8 items-center bg-indigo-50 px-3 text-sm font-medium text-indigo-700 outline-none hover:bg-indigo-100 focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-500 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20 dark:disabled:text-slate-500"
          :disabled="takingOver"
          @click="takeOverLock"
        >
          Locked by {{ lockHolder }} · take over
        </button>
      </div>

      <section v-if="tabbedParts">
        <div class="border-b border-slate-200 dark:border-slate-800">
          <div
            class="-mb-px flex overflow-x-auto"
            role="tablist"
            aria-label="Task content"
          >
            <button
              v-for="part in partSections"
              :id="partTabId(part.field, 'top')"
              :key="part.field"
              type="button"
              role="tab"
              :aria-controls="partPanelId(part.field)"
              :aria-selected="activePartField === part.field"
              :tabindex="activePartField === part.field ? 0 : -1"
              :class="partTabClass(part.field)"
              @click="selectPart(part.field)"
              @keydown="handlePartTabKeydown($event, part.field, 'top')"
            >
              {{ part.label }}
            </button>
          </div>
        </div>

        <div
          v-for="part in partSections"
          v-show="activePartField === part.field"
          :id="partPanelId(part.field)"
          :key="part.field"
          class="mt-3"
          role="tabpanel"
          :aria-labelledby="partTabId(part.field, 'top')"
          :tabindex="activePartField === part.field ? 0 : -1"
        >
          <DescriptionEditor
            v-model="draft[part.field]"
            :can-edit="canEdit"
          />
        </div>

        <div class="mt-3 border-b border-slate-200 dark:border-slate-800">
          <div
            class="-mb-px flex overflow-x-auto"
            role="tablist"
            aria-label="Task content, bottom"
          >
            <button
              v-for="part in partSections"
              :id="partTabId(part.field, 'bottom')"
              :key="part.field"
              type="button"
              role="tab"
              :aria-controls="partPanelId(part.field)"
              :aria-selected="activePartField === part.field"
              :tabindex="activePartField === part.field ? 0 : -1"
              :class="partTabClass(part.field)"
              @click="selectPart(part.field)"
              @keydown="handlePartTabKeydown($event, part.field, 'bottom')"
            >
              {{ part.label }}
            </button>
          </div>
        </div>
      </section>

      <template v-else>
        <section
          v-for="part in partSections"
          :key="part.field"
          class="space-y-3"
        >
          <h2 class="text-sm font-semibold uppercase text-slate-500 dark:text-slate-400">
            {{ part.label }}
          </h2>
          <DescriptionEditor
            v-model="draft[part.field]"
            :can-edit="canEdit"
          />
        </section>
      </template>

      <footer class="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
        <div class="flex flex-wrap items-center gap-2">
          <span>Updated {{ formatDate(task.updated_at) }}</span>
          <button
            v-if="saveError"
            type="button"
            class="font-medium text-red-700 outline-none hover:text-red-800 focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:text-slate-400 dark:text-red-300 dark:hover:text-red-200 dark:disabled:text-slate-500"
            :disabled="!canEdit"
            @click="retrySave"
          >
            Couldn't save · retry
          </button>
        </div>

        <div class="flex items-center gap-3">
          <button
            v-if="confirmingDelete"
            type="button"
            class="font-medium text-slate-600 outline-none hover:text-slate-950 focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-400 dark:text-slate-300 dark:hover:text-slate-100 dark:disabled:text-slate-500"
            :disabled="!canEdit"
            @click="confirmingDelete = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="font-semibold text-red-700 outline-none hover:text-red-800 focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:text-slate-400 dark:text-red-300 dark:hover:text-red-200 dark:disabled:text-slate-500"
            :disabled="!canEdit || deleting"
            @click="deleteCurrentTask"
          >
            {{ deleteButtonLabel }}
          </button>
        </div>
      </footer>
    </div>

    <div v-else class="py-12 text-sm text-slate-500 dark:text-slate-400">
      Task not found.
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import DescriptionEditor from './DescriptionEditor.vue'
import GutScorePicker from './GutScorePicker.vue'
import Dropdown from './ui/Dropdown.vue'
import { visibleTaskTags } from './tags'

const props = defineProps({
  store: {
    type: Object,
    required: true,
  },
  taskId: {
    type: [String, Number],
    required: true,
  },
  canEdit: {
    type: Boolean,
    required: true,
  },
  lockHolder: {
    type: String,
    default: null,
  },
  tabbedParts: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['request-close', 'deleted', 'moved'])

const titleInputRef = ref(null)
const effortInputRef = ref(null)
const tagInputRef = ref(null)
const editingEffort = ref(false)
const addingTag = ref(false)
const newTag = ref('')
const saveError = ref('')
const deleting = ref(false)
const confirmingDelete = ref(false)
const takingOver = ref(false)
const syncingDraft = ref(false)
const draftTaskId = ref(null)
const pendingSaveCount = ref(0)
const showParkScores = ref(false)
const parkingTask = ref(false)
const parkMessage = ref('')
const parkMessageTone = ref('ok')
const showMovePanel = ref(false)
const movingTask = ref(false)
const moveWorkspaceSlug = ref('')
const moveProjectSlug = ref('')
const moveError = ref('')
const moveMessage = ref('')
const activePartField = ref('original_idea')
const partSaveTimers = new Map()
let saveQueue = Promise.resolve()

const partSections = [
  { field: 'original_idea', label: 'Original idea' },
  { field: 'notes', label: 'Notes' },
  { field: 'shaped_idea', label: 'Shaped idea' },
  { field: 'promoted_task', label: 'Promoted task' },
]
const partFields = partSections.map((part) => part.field)
const IDEA_PHASE_STATES = ['', 'captured', 'shaping', 'shaped']

const draft = reactive({
  ref: '',
  title: '',
  original_idea: '',
  notes: '',
  shaped_idea: '',
  promoted_task: '',
  status: 'backlog',
  planning_state: '',
  project_id: '',
  effort: '',
  gut_score: null,
  tags: [],
})

const lastSaved = reactive({
  ref: '',
  title: '',
  original_idea: '',
  notes: '',
  shaped_idea: '',
  promoted_task: '',
  status: 'backlog',
  planning_state: '',
  project_id: '',
  effort: '',
  gut_score: null,
  tags: [],
})

const currentTaskId = computed(() => normalizeId(props.taskId))
const loading = computed(() => props.store.detailLoading.value)
const projects = computed(() => props.store.projects?.value || [])
const moveTargets = computed(() => props.store.moveTargets?.value || [])
const task = computed(() => {
  const detail = props.store.activeTaskDetail.value
  return normalizeId(detail?.id) === currentTaskId.value ? detail : null
})
const currentWorkspaceSlug = computed(() => task.value?.workspace?.slug || props.store.workspaceSlug || '')
const currentProjectSlug = computed(() => task.value?.project?.slug || '')
const statusOptions = computed(() => props.store.statuses.map((status) => ({
  value: status,
  label: statusLabel(status),
})))
const planningStateOptions = computed(() => [
  { value: '', label: 'No Planning State' },
  ...['captured', 'shaping', 'shaped', 'parked', 'promoted'].map((planningState) => ({
    value: planningState,
    label: fieldLabel(planningState),
  })),
])
const projectOptions = computed(() => [
  { value: '', label: 'No project' },
  ...projects.value.map((project) => ({ value: String(project.id), label: project.name })),
])
const moveWorkspaceOptions = computed(() => moveTargets.value.map((workspace) => ({
  value: workspace.slug,
  label: workspace.name,
})))
const selectedMoveTarget = computed(() => moveTargets.value.find((workspace) => workspace.slug === moveWorkspaceSlug.value) || null)
const selectedMoveProject = computed(() => selectedMoveTarget.value?.projects?.find((project) => project.slug === moveProjectSlug.value) || null)
const moveProjectOptions = computed(() => {
  const target = selectedMoveTarget.value
  if (!target) return []

  const options = target.workspaceMember ? [{ value: '', label: 'No project' }] : []
  return [
    ...options,
    ...(target.projects || []).map((project) => ({ value: project.slug, label: project.name })),
  ]
})
const moveWorkspaceName = computed(() => selectedMoveTarget.value?.name || 'Workspace')
const moveProjectName = computed(() => {
  if (selectedMoveProject.value) return selectedMoveProject.value.name
  if (moveProjectSlug.value) return 'Project'
  return selectedMoveTarget.value?.workspaceMember ? 'No project' : 'Project required'
})
const moveRequiresProject = computed(() => Boolean(selectedMoveTarget.value && !selectedMoveTarget.value.workspaceMember))
const moveSelectionChanged = computed(() => (
  Boolean(selectedMoveTarget.value) &&
    (moveWorkspaceSlug.value !== currentWorkspaceSlug.value || moveProjectSlug.value !== currentProjectSlug.value)
))
const canMoveTask = computed(() => props.canEdit && moveTargets.value.length > 0)
const canSubmitMove = computed(() => {
  if (!canMoveTask.value || movingTask.value || !moveSelectionChanged.value) return false
  if (moveRequiresProject.value && !moveProjectSlug.value) return false

  return true
})
const visibleTags = computed(() => visibleTaskTags(draft.tags))
const canParkTask = computed(() => props.canEdit && Boolean(task.value) && IDEA_PHASE_STATES.includes(draft.planning_state || ''))
const parkMessageClass = computed(() => {
  return parkMessageTone.value === 'error'
    ? 'text-red-700 dark:text-red-300'
    : 'text-slate-500 dark:text-slate-400'
})
const deleteButtonLabel = computed(() => {
  if (deleting.value) return 'Deleting'
  return confirmingDelete.value ? 'Confirm delete' : 'Delete'
})

watch(
  () => task.value?.id,
  () => {
    activePartField.value = partSections[0].field
    syncDraft(task.value)
    resetParkControl()
    resetMoveControl()
  },
  { immediate: true }
)

partFields.forEach((field) => {
  watch(
    () => draft[field],
    () => {
      if (syncingDraft.value || !props.canEdit) return
      schedulePartSave(field)
    }
  )
})

watch(
  () => props.canEdit,
  (canEdit) => {
    if (canEdit) return

    editingEffort.value = false
    addingTag.value = false
    confirmingDelete.value = false
    showParkScores.value = false
    parkingTask.value = false
    showMovePanel.value = false
    movingTask.value = false
  }
)

onBeforeUnmount(() => {
  clearPartSaveTimers()
})

defineExpose({
  flushPendingSave,
  hasUnsavedChanges,
})

function syncDraft(sourceTask) {
  if (!sourceTask) return

  const snapshot = snapshotFromTask(sourceTask)
  draftTaskId.value = normalizeId(sourceTask.id)
  syncingDraft.value = true
  Object.assign(draft, cloneSnapshot(snapshot))
  Object.assign(lastSaved, cloneSnapshot(snapshot))
  nextTick(() => {
    syncingDraft.value = false
  })
}

function snapshotFromTask(sourceTask) {
  return {
    ref: sourceTask.ref || '',
    title: sourceTask.title || '',
    original_idea: sourceTask.original_idea || '',
    notes: sourceTask.notes || '',
    shaped_idea: sourceTask.shaped_idea || '',
    promoted_task: sourceTask.promoted_task || '',
    status: sourceTask.status || 'backlog',
    planning_state: sourceTask.planning_state || '',
    project_id: projectIdFor(sourceTask.project),
    effort: sourceTask.effort ?? '',
    gut_score: sourceTask.gut_score ?? null,
    tags: [...(sourceTask.tags || [])],
  }
}

function cloneSnapshot(snapshot) {
  return {
    ...snapshot,
    tags: [...(snapshot.tags || [])],
  }
}

function selectPart(field) {
  if (!partFields.includes(field)) return
  activePartField.value = field
}

async function handlePartTabKeydown(event, field, placement) {
  const currentIndex = partSections.findIndex((part) => part.field === field)
  if (currentIndex < 0) return

  let nextIndex = null
  if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % partSections.length
  if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + partSections.length) % partSections.length
  if (event.key === 'Home') nextIndex = 0
  if (event.key === 'End') nextIndex = partSections.length - 1
  if (nextIndex === null) return

  event.preventDefault()
  const nextField = partSections[nextIndex].field
  selectPart(nextField)
  await nextTick()
  document.getElementById(partTabId(nextField, placement))?.focus()
}

function partTabId(field, placement) {
  return `task-${currentTaskId.value || 'detail'}-${field}-${placement}-tab`
}

function partPanelId(field) {
  return `task-${currentTaskId.value || 'detail'}-${field}-panel`
}

function partTabClass(field) {
  const base = 'shrink-0 border-b-2 px-3 py-2.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500'
  return activePartField.value === field
    ? `${base} border-indigo-600 text-indigo-700 dark:border-indigo-400 dark:text-indigo-300`
    : `${base} border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-950 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-100`
}

function commitTitle() {
  titleInputRef.value?.blur()
}

async function saveDirtyFields(fields = null) {
  if (!props.canEdit || !task.value) return null

  const taskId = currentTaskId.value
  return enqueueSave(taskId, () => dirtyPayload(fields))
}

function retrySave() {
  saveDirtyFields()
}

function toggleParkScores() {
  if (!canParkTask.value || parkingTask.value) return

  showParkScores.value = !showParkScores.value
  parkMessage.value = ''
  parkMessageTone.value = 'ok'
}

async function parkWithScore(score) {
  if (!canParkTask.value || parkingTask.value) return

  const taskId = currentTaskId.value
  parkingTask.value = true
  parkMessage.value = ''
  parkMessageTone.value = 'ok'

  try {
    const savedTask = await props.store.updateTask(taskId, {
      planning_state: 'parked',
      gut_score: score,
      status: 'backlog',
    })
    if (draftTaskId.value === taskId) syncServerInvariantFields(savedTask)
    showParkScores.value = false
    parkMessage.value = 'Parked. Sleep well.'
  } catch (requestError) {
    parkMessageTone.value = 'error'
    parkMessage.value = requestError?.status === 423 ? 'Task is locked' : (requestError?.message || 'Could not park task.')
  } finally {
    parkingTask.value = false
  }
}

function resetParkControl() {
  showParkScores.value = false
  parkingTask.value = false
  parkMessage.value = ''
  parkMessageTone.value = 'ok'
}

function toggleMovePanel() {
  if (!canMoveTask.value || movingTask.value) return

  if (showMovePanel.value) {
    cancelMove()
  } else {
    resetMoveControl()
    showMovePanel.value = true
  }
}

function cancelMove() {
  if (movingTask.value) return

  showMovePanel.value = false
  resetMoveControl()
}

function resetMoveControl() {
  const targetSlug = currentWorkspaceSlug.value || moveTargets.value[0]?.slug || ''
  moveWorkspaceSlug.value = targetSlug
  moveProjectSlug.value = currentProjectSlug.value
  moveError.value = ''
  moveMessage.value = ''

  normalizeMoveProjectSelection()
}

function handleMoveWorkspaceSelect(option) {
  moveWorkspaceSlug.value = option?.value || ''
  moveError.value = ''
  moveMessage.value = ''
  normalizeMoveProjectSelection({ preferCurrent: false })
}

function normalizeMoveProjectSelection({ preferCurrent = true } = {}) {
  const target = selectedMoveTarget.value
  if (!target) {
    moveProjectSlug.value = ''
    return
  }

  const currentProjectStillVisible = preferCurrent &&
    target.slug === currentWorkspaceSlug.value &&
    (target.projects || []).some((project) => project.slug === currentProjectSlug.value)

  if (currentProjectStillVisible) {
    moveProjectSlug.value = currentProjectSlug.value
    return
  }

  if (target.workspaceMember) {
    if (!moveProjectSlug.value || !(target.projects || []).some((project) => project.slug === moveProjectSlug.value)) {
      moveProjectSlug.value = ''
    }
    return
  }

  if (!(target.projects || []).some((project) => project.slug === moveProjectSlug.value)) {
    moveProjectSlug.value = target.projects?.[0]?.slug || ''
  }
}

async function submitMove() {
  if (!canSubmitMove.value || !task.value) return

  const taskId = currentTaskId.value
  movingTask.value = true
  moveError.value = ''
  moveMessage.value = ''

  try {
    await flushPendingSave(taskId)
    const movedTask = await props.store.moveTask(taskId, {
      workspace: moveWorkspaceSlug.value,
      project: moveProjectSlug.value || null,
    })

    if (movedTask?.workspace?.slug === props.store.workspaceSlug) {
      syncDraft(movedTask)
      showMovePanel.value = false
      moveMessage.value = 'Moved.'
    } else {
      emit('moved', movedTask)
    }
  } catch (requestError) {
    moveError.value = requestError?.message || 'Could not move task.'
  } finally {
    movingTask.value = false
  }
}

function schedulePartSave(field) {
  const taskId = currentTaskId.value
  clearPartSaveTimer(field)
  const timer = window.setTimeout(() => {
    partSaveTimers.delete(field)
    enqueueSave(taskId, () => dirtyPayload([field]))
  }, 700)
  partSaveTimers.set(field, timer)
}

function clearPartSaveTimer(field) {
  const timer = partSaveTimers.get(field)
  if (!timer) return

  window.clearTimeout(timer)
  partSaveTimers.delete(field)
}

function clearPartSaveTimers() {
  partSaveTimers.forEach((timer) => window.clearTimeout(timer))
  partSaveTimers.clear()
}

async function flushPendingSave(explicitTaskId = currentTaskId.value) {
  const taskId = taskIdForSave(explicitTaskId)
  clearPartSaveTimers()
  await enqueueSave(taskId, () => dirtyPayload())
}

function enqueueSave(taskId, payloadBuilder) {
  pendingSaveCount.value += 1
  saveQueue = saveQueue.catch(() => null).then(async () => {
    if (!taskId || draftTaskId.value !== taskId) return null

    const payload = payloadBuilder()
    if (!payload || !Object.keys(payload).length) return null

    saveError.value = ''
    const savedTask = await props.store.updateTask(taskId, payload)
    if (draftTaskId.value === taskId) {
      Object.assign(lastSaved, cloneSnapshot(snapshotFromTask(savedTask)))
      syncServerInvariantFields(savedTask)
    }
    return savedTask
  }).catch((requestError) => {
    saveError.value = requestError?.message || 'Task could not be saved'
    throw requestError
  }).finally(() => {
    pendingSaveCount.value -= 1
  })

  return saveQueue
}

function syncServerInvariantFields(savedTask) {
  if (!savedTask) return

  const fields = ['status', 'planning_state', 'gut_score']
  fields.forEach((field) => {
    draft[field] = savedTask[field]
    lastSaved[field] = savedTask[field]
  })
}

function hasUnsavedChanges() {
  return partSaveTimers.size > 0 || pendingSaveCount.value > 0 || hasDirtyFields()
}

function hasDirtyFields() {
  if (!draftTaskId.value) return false

  return draft.title.trim() !== lastSaved.title ||
    partFields.some((field) => draft[field] !== lastSaved[field]) ||
    draft.status !== lastSaved.status ||
    draft.planning_state !== lastSaved.planning_state ||
    draft.project_id !== lastSaved.project_id ||
    normalizeHours(draft.effort) !== normalizeHours(lastSaved.effort) ||
    JSON.stringify(draft.tags) !== JSON.stringify(lastSaved.tags)
}

function taskIdForSave(id) {
  return normalizeId(id) || currentTaskId.value
}

function dirtyPayload(fields = null) {
  const allowed = fields ? new Set(fields) : null
  const payload = {}

  if (shouldSave('title', allowed)) {
    const title = draft.title.trim()
    if (!title) {
      draft.title = lastSaved.title
    } else if (title !== lastSaved.title) {
      draft.title = title
      payload.title = title
    }
  }

  partFields.forEach((field) => {
    if (shouldSave(field, allowed) && draft[field] !== lastSaved[field]) {
      payload[field] = draft[field]
    }
  })

  if (shouldSave('status', allowed) && draft.status !== lastSaved.status) {
    payload.status = draft.status
  }

  if (shouldSave('planning_state', allowed) && draft.planning_state !== lastSaved.planning_state) {
    payload.planning_state = draft.planning_state || null
  }

  if (shouldSave('project_id', allowed) && draft.project_id !== lastSaved.project_id) {
    payload.project_id = draft.project_id || null
  }

  if (shouldSave('effort', allowed) && normalizeHours(draft.effort) !== normalizeHours(lastSaved.effort)) {
    payload.effort = normalizeHours(draft.effort)
  }

  if (shouldSave('tags', allowed) && JSON.stringify(draft.tags) !== JSON.stringify(lastSaved.tags)) {
    payload.tags = [...draft.tags]
  }

  return payload
}

function shouldSave(field, allowed) {
  return !allowed || allowed.has(field)
}

function normalizeHours(value) {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  return Number.isNaN(numeric) ? null : numeric
}

async function startEffort() {
  if (!props.canEdit) return

  editingEffort.value = true
  await nextTick()
  effortInputRef.value?.focus()
  effortInputRef.value?.select()
}

function finishEffort() {
  editingEffort.value = false
  saveDirtyFields(['effort'])
}

function cancelEffort(event) {
  event.stopPropagation()
  draft.effort = lastSaved.effort
  editingEffort.value = false
}

async function startTagInput() {
  if (!props.canEdit) return

  addingTag.value = true
  newTag.value = ''
  await nextTick()
  tagInputRef.value?.focus()
}

function finishTagInput() {
  if (newTag.value) {
    addTag()
  } else {
    addingTag.value = false
  }
}

function cancelTagInput(event) {
  event.stopPropagation()
  newTag.value = ''
  addingTag.value = false
}

function addTag() {
  if (!props.canEdit || !newTag.value) return

  if (!draft.tags.includes(newTag.value)) {
    draft.tags = [...draft.tags, newTag.value]
    saveDirtyFields(['tags'])
  }

  newTag.value = ''
  addingTag.value = false
}

function removeTag(tag) {
  if (!props.canEdit) return

  draft.tags = draft.tags.filter((item) => item !== tag)
  saveDirtyFields(['tags'])
}

async function deleteCurrentTask() {
  if (!props.canEdit || !task.value) return

  if (!confirmingDelete.value) {
    confirmingDelete.value = true
    return
  }

  deleting.value = true
  saveError.value = ''
  const deletedTaskId = task.value.id

  try {
    await flushPendingSave(deletedTaskId)
    await props.store.deleteTask(deletedTaskId)
    emit('deleted', deletedTaskId)
  } catch (requestError) {
    saveError.value = requestError?.message || 'Task could not be deleted'
  } finally {
    deleting.value = false
  }
}

async function takeOverLock() {
  if (!currentTaskId.value || takingOver.value) return

  takingOver.value = true
  saveError.value = ''

  try {
    await props.store.forceUnlockTask(currentTaskId.value)
    await props.store.lockTask(currentTaskId.value, { locked_via: 'web' })
    await props.store.loadTaskDetail(currentTaskId.value)
  } catch (requestError) {
    saveError.value = requestError?.message || 'Task lock could not be taken over'
  } finally {
    takingOver.value = false
  }
}

function projectIdFor(project) {
  if (!project) return ''

  const matchedProject = projects.value.find((item) => item.slug === project.slug)
  return matchedProject?.id ? String(matchedProject.id) : ''
}

function projectName(projectId) {
  if (!projectId) return 'No project'

  return projects.value.find((project) => String(project.id) === String(projectId))?.name || 'No project'
}

function normalizeId(id) {
  return id === null || id === undefined ? null : String(id)
}

function statusLabel(status) {
  return (status || '').replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function fieldLabel(value) {
  return statusLabel(value)
}

function planningStateLabel(value) {
  return value ? fieldLabel(value) : 'No Planning State'
}

function statusDotClass(status) {
  const tones = {
    backlog: 'border-slate-300 bg-slate-200 dark:border-slate-600 dark:bg-slate-700',
    todo: 'border-slate-400 bg-slate-50 dark:border-slate-500 dark:bg-slate-900',
    in_progress: 'border-indigo-300 bg-indigo-100 dark:border-indigo-500 dark:bg-indigo-500/25',
    review: 'border-indigo-400 bg-indigo-200 dark:border-indigo-400 dark:bg-indigo-400/30',
    done: 'border-indigo-600 bg-indigo-600 dark:border-indigo-400 dark:bg-indigo-400',
    cancelled: 'border-slate-500 bg-slate-500 dark:border-slate-500 dark:bg-slate-500',
  }

  return ['h-2.5 w-2.5 shrink-0 border', tones[status] || tones.todo]
}

function chipButtonClass(disabled = false) {
  return [
    'inline-flex min-h-8 items-center gap-2 bg-slate-100 px-3 text-sm font-medium text-slate-700 outline-none hover:bg-slate-200 hover:text-slate-950 focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-500 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-slate-100 dark:disabled:text-slate-500',
    disabled ? 'hover:bg-slate-100 hover:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-500' : '',
  ]
}

function moveDropdownClass(disabled = false) {
  return [
    'inline-flex min-h-9 min-w-44 max-w-64 items-center justify-between gap-2 border border-slate-300 bg-slate-50 px-3 text-left text-sm font-medium text-slate-700 outline-none hover:bg-slate-100 focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:disabled:text-slate-500',
    disabled ? 'hover:bg-slate-50 dark:hover:bg-slate-900' : '',
  ]
}

function formatHours(value) {
  if (value === null || value === undefined || value === '') return '0h'
  return `${Number(value).toLocaleString()}h`
}

function formatDate(value) {
  if (!value) return ''
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}
</script>
